import { FolderTreeSelect } from '../../CommonComponents/FolderTreeSelect/FolderTreeSelect';
import { FOLDER_SELECT_MENU_HEIGHT } from '@opengov/stories-shared/constants';
import { RouteComponentProps } from '@reach/router';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useMemo, useState } from 'react';
import { IStoriesFolder, IStoryListMessages } from '../../../../types';
import { navigateToFolderList } from '../../../state/navigation';
import { transformFolderStructureToMap } from '../../../utils';

import { styles } from './CreateFolderModal.styles';
import { useCreateStoryFolder } from '../hooks';
import { useToast } from '../../Toast/context/useToast.context';

export interface ICreateFolderModalProps extends RouteComponentProps {
  currentFolderId?: string;
  rootFolderId: string;
  entityId: number;
  entitySubdomain: string;
  folderStructure: IStoriesFolder[];
  messages: IStoryListMessages;
  onClose(): void;
}

const IN_FOLDER = 'IN_FOLDER';
const NO_FOLDER = 'NO_FOLDER';

export const CreateFolderModal: React.FC<ICreateFolderModalProps> = props => {
  const { currentFolderId, entityId, entitySubdomain, messages, folderStructure } = props;
  const { toggleToast } = useToast();
  const [folderName, setFolderName] = useState('');
  const [isTooDeep, setIsTooDeep] = useState(false);
  const [hasSameName, setHasSameName] = useState(false);
  const [location, setLocation] = useState(currentFolderId ? IN_FOLDER : NO_FOLDER);
  const [parentFolderId, setParentFolderId] = useState(
    currentFolderId ? currentFolderId : folderStructure && folderStructure[0] && folderStructure[0].id
  );
  const folderMap = useMemo(() => transformFolderStructureToMap(folderStructure), [folderStructure]);
  const trimmedFolderName = useMemo(() => folderName.trim(), [folderName]);
  const createStoryFolderMutation = useCreateStoryFolder({
    title: messages.somethingWentWrongError,
    body: messages.storyFolderCreateFailed
  });

  useEffect(() => {
    if (location === IN_FOLDER && folderMap[parentFolderId]) {
      setIsTooDeep(folderMap[parentFolderId].depth > 6);
    } else {
      setIsTooDeep(false);
    }
  }, [location, parentFolderId]);

  useEffect(() => {
    if (location === IN_FOLDER) {
      const parentFolder = folderMap[parentFolderId];
      const siblings = parentFolder.children || [];
      const hasMatch = !!siblings.find(folder => folder.displayName === trimmedFolderName);
      setHasSameName(hasMatch);
    }
  }, [trimmedFolderName, location]);

  const onConfirmCreateFolder = () => {
    createStoryFolderMutation.mutate(
      {
        storyFolder: {
          entityId,
          title: trimmedFolderName,
          parentFolderId: location === IN_FOLDER ? parentFolderId : props.rootFolderId
        }
      },
      {
        // Stay mounted until the request settles so the new folder id is
        // available to open it in the list.
        onSuccess: (response) => {
          props.onClose();
          toggleToast({
            title: messages.storyFolderCreated,
            body: messages.storyFolderCreatedDescription({ title: trimmedFolderName }),
            severity: 'success'
          });
          navigateToFolderList(entitySubdomain, response.insert_storyFolders_one.id);
        },
        onError: () => props.onClose()
      }
    );
  };

  const errorMessage = isTooDeep
    ? messages.nestedTooDeep
    : hasSameName
      ? messages.noFoldersWithSameName
      : undefined;

  return (
    <div data-test="create-folder">
      <Dialog open={true} onClose={props.onClose} fullWidth maxWidth="sm" PaperProps={{ sx: styles.dialogPaper }} aria-labelledby="create-folder-dialog-title">
        <DialogTitle id="create-folder-dialog-title" sx={styles.dialogTitle}>
          {messages.createFolder}
          <IconButton aria-label="close" onClick={props.onClose} size="small" sx={styles.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Box>
            <Box sx={styles.labelRow}>
              <Typography id="create-folder-name-label" variant="body2" sx={styles.formLabel}>
                {messages.name}
              </Typography>
              <Typography variant="caption" sx={styles.characterCounter}>
                {trimmedFolderName.length}/60
              </Typography>
            </Box>
            <TextField
              fullWidth
              required
              size="small"
              sx={styles.textField}
              placeholder={messages.folderName}
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              inputProps={{ 'data-test': 'name-input', maxLength: 60, className: 'no-format', 'aria-labelledby': 'create-folder-name-label' }}
            />
          </Box>

          <Box sx={styles.spacer}>
            <FormControl component="fieldset" sx={styles.fieldset}>
              <FormLabel component="legend" sx={styles.formLabel}>
                {messages.location}
              </FormLabel>
              <RadioGroup
                sx={styles.radioGroup}
                value={location}
                onChange={(_e, value) => setLocation(value)}
                data-test="location-radio-group"
              >
                <FormControlLabel
                  value={NO_FOLDER}
                  sx={styles.radioButton}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'location-option-no-folder', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={messages.noFolder}
                />
                <FormControlLabel
                  value={IN_FOLDER}
                  sx={styles.radioButton}
                  disabled={folderStructure.length < 1}
                  control={
                    <Radio
                      inputProps={{ 'data-test': 'location-option-in-folder', className: 'no-format' } as React.InputHTMLAttributes<HTMLInputElement>}
                    />
                  }
                  label={
                    <Box>
                      {messages.inFolder}
                      {location === IN_FOLDER && (
                        <Box sx={styles.locationSelectContainer}>
                          <FolderTreeSelect
                            activeNodeId={parentFolderId}
                            data={folderStructure}
                            dataTest="folder-accordion-select"
                            disabled={!folderStructure || location !== IN_FOLDER}
                            maxMenuHeight={FOLDER_SELECT_MENU_HEIGHT}
                            onNodeSelect={node => setParentFolderId(node.id)}
                            showFilter={true}
                            filterPlaceholder={messages.filterFolders}
                          />
                        </Box>
                      )}
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {errorMessage && (
            <FormHelperText error sx={styles.errorMsg}>
              {errorMessage}
            </FormHelperText>
          )}
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Button variant="outlined" color="secondary" size="small" onClick={props.onClose} data-test="cancel-button">
            {messages.cancel}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={
              trimmedFolderName.length < 3 ||
              trimmedFolderName.length > 60 ||
              isTooDeep ||
              hasSameName ||
              createStoryFolderMutation.isLoading
            }
            onClick={onConfirmCreateFolder}
            data-test="create-button"
          >
            {messages.create}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
